import React, { useState, useEffect } from 'react';
import { Search, Phone, MapPin, CheckCircle, XCircle, AlertCircle, Globe, Users, Database, CreditCard, Clock, Star, Shield } from 'lucide-react';

const PhoneVerifierApp = () => {
  const [sessionData, setSessionData] = useState(null);
  const [searchNumber, setSearchNumber] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3503" || 'https://jedome-backend.onrender.com';

  const cities = {
    'Numéros douala': { name: 'Douala', icon: '🏙️', color: 'blue' },
    'Numéros yaounde': { name: 'Yaoundé', icon: '🏛️', color: 'purple' },
    'Numéros bafoussam': { name: 'Bafoussam', icon: '🌄', color: 'green' },
    'Numéros kribi': { name: 'Kribi', icon: '🏖️', color: 'cyan' },
    'Numéros bertoua': { name: 'Bertoua', icon: '🌳', color: 'orange' },
    'Numéros limbe': { name: 'Limbe', icon: '🌳', color: 'cyan' },
  };

  // Générer des informations du navigateur pour le fingerprinting
  const getBrowserFingerprint = async () => {
    // Fonction pour obtenir l'adresse IP publique
    const getPublicIP = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        console.log('IP:', data.ip);
        return data.ip;
      } catch (error) {
        console.error('Erreur lors de la récupération de l\'IP:', error);
        return null;
      }
    };

    const ip = await getPublicIP();
    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      ip: ip // Ajoute l'IP publique
    };
  };

  // Initialiser la session au démarrage
  useEffect(() => {
    initializeSession();
  }, []);

  const initializeSession = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/546789758989/4566767767654645456434534/654654654657897518941489494894/init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(await getBrowserFingerprint())
      });

      const data = await response.json();

      if (data.success) {
        setSessionData(data);
        console.log('Session initialisée:', data);
        // Afficher le modal des conditions d'utilisation après l'initialisation
        setShowTermsModal(true);
      } else {
        throw new Error("Erreur lors de l'initialisation");
      }
    } catch (error) {
      console.error('Erreur d\'initialisation:', error);
      alert('Erreur de connexion au serveur. Veuillez rafraîchir la page.');
    } finally {
      setIsLoading(false);
    }
  };

  const updateSession = async (paymentPhone) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/546789758989/4566767767654645456434534/654654654657897518941489494894/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(await getBrowserFingerprint())
      });

      const data = await response.json();

      if (data.success) {
        setSessionData(data);
        await handleSearch(paymentPhone);
        console.log('Session initialisée:', data);
      } else {
        throw new Error("Erreur lors de la mise à jour");
      }
    } catch (error) {
      console.error('Erreur d\'initialisation:', error);
      alert('Erreur de connexion au serveur. Veuillez rafraîchir la page.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (phone = null) => {
    if (!searchNumber.trim()) {
      alert('Veuillez entrer un numéro de téléphone');
      return;
    }

    if (!sessionData) {
      alert('Session non initialisée. Veuillez rafraîchir la page.');
      return;
    }

    setIsSearching(true);

    try {
      const response = await fetch(`${API_BASE_URL}/546789758989/4566767767654645456434534/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: searchNumber.replace(/\s/g, ''),
          payPhoneNumber: phone ? phone.replace(/\s/g, '') : null,
          sessionId: sessionData.sessionId
        })
      });

      const data = await response.json();

      if (response.status === 402) {
        // Paiement requis
        setShowPaymentModal(true);
        return;
      }

      if (data.success) {
        const result = {
          number: searchNumber,
          found: data.found,
          data: data.data,
          timestamp: new Date().toLocaleTimeString()
        };

        setSearchResult(result);
        setSearchHistory(prev => [result, ...prev.slice(0, 4)]);

        // Mettre à jour les données de session
        setSessionData(prev => ({
          ...prev,
          freeSearchesRemaining: data.freeSearchesRemaining,
          isPremium: data.isPremium
        }));
      } else {
        setShowPaymentModal(true);
        // alert(data.error || 'Erreur lors de la recherche');
      }
    } catch (error) {
      console.error('Erreur de recherche:', error);
      alert('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setIsSearching(false);
    }
  };

  const initiatePayment = async (paymentPhone, operator = 'AUTO') => {
    setIsPaymentLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/546789758989/4566767767654645456434534/1546474871954984548979/init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: sessionData.sessionId,
          phoneNumber: paymentPhone.replace(/\s/g, ''),
          operator: operator,
          searchPhoneNumber: searchNumber.replace(/\s/g, ''),
        })
      });

      const data = await response.json();

      if (response.status === 200 || response.status === 201) {
        // if(true){
        // setPaymentData(data);
        // setPaymentStatus('pending');

        setSessionData(prev => ({
          ...prev,
          isPremium: true,
          freeSearchesRemaining: 1
        }));

        // Fermer le modal après 2 secondes et relancer une nouvelle session
        setTimeout(async () => {
          setShowPaymentModal(false);
          setPaymentData(null);
          setPaymentStatus(null);

          // Relancer une nouvelle session
          await updateSession(paymentPhone.replace(/\s/g, ''));
        }, 2000);

      } else {
        // Simuler l'attente du paiement
        setTimeout(() => {
          checkPaymentStatus(data.transactionId);
        }, 2000);
        alert(data.error || "Erreur lors de l'initiation du paiement");
      }
    } catch (error) {
      console.error('Erreur de paiement:', error);
      alert('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setIsPaymentLoading(false);
    }
  };

  const checkPaymentStatus = async (transactionId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/546789758989/4566767767654645456434534/1546474871954984548979/status/${sessionData.sessionId}/${transactionId}`
      );

      const data = await response.json();

      if (data.success) {
        setPaymentStatus(data.status);

        if (data.status === 'success') {
          setSessionData(prev => ({
            ...prev,
            isPremium: true,
            freeSearchesRemaining: 'unlimited'
          }));

          // Fermer le modal après 2 secondes et relancer une nouvelle session
          setTimeout(async () => {
            setShowPaymentModal(false);
            setPaymentData(null);
            setPaymentStatus(null);

            // Relancer une nouvelle session
            await initializeSession();
          }, 2000);
        } else if (data.status === 'pending') {
          // Continuer à vérifier
          setTimeout(() => {
            checkPaymentStatus(transactionId);
          }, 3000);
        }
      }
    } catch (error) {
      console.error('Erreur vérification paiement:', error);
    }
  };

  const formatPhoneDisplay = (number) => {
    const cleaned = number.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('237')) {
      const withoutCountryCode = cleaned.substring(3);
      return `+237 ${withoutCountryCode.slice(0, 1)} ${withoutCountryCode.slice(1, 3)} ${withoutCountryCode.slice(3, 5)} ${withoutCountryCode.slice(5, 7)} ${withoutCountryCode.slice(7)}`;
    }
    return number;
  };

  const getOperatorColor = (operator) => {
    const colors = {
      'Orange': 'orange',
      'MTN': 'yellow',
      'Camtel': 'green'
    };
    return colors[operator] || 'gray';
  };

  const handleClosePaymentModal = () => {
    // Ne fermer le modal que si pas de paiement en cours
    if (paymentStatus !== 'pending') {
      setShowPaymentModal(false);
      setPaymentData(null);
      setPaymentStatus(null);
      setIsPaymentLoading(false);
    }
  };

  const handleAcceptTerms = () => {
    console.log('Conditions acceptées');
    setTermsAccepted(true);
    setShowTermsModal(false);
  };

  const handleRejectTerms = () => {
    alert('Vous devez accepter les conditions d\'utilisation pour continuer.');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mb-6 shadow-lg animate-pulse">
            <Database className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Initialisation...</h2>
          <div className="w-64 bg-gray-200 rounded-full h-2 mb-4 mx-auto">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
          </div>
          <p className="text-gray-600">Connexion sécurisée en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header avec statut premium */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-4 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mb-6 shadow-lg">
            {/* <Phone className="w-10 h-10 text-white" /> */}
            <img src="https://jedome-front.vercel.app/logo/logo.png" alt="logo" className="w-20 h-20" />

          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-3">
            Vérificateur de Numéros
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            Vérifiez instantanément vos contacts sur Jedolo
          </p>

          {/* Statut utilisateur */}
          <div className="max-w-md mx-auto mb-6">
            {sessionData?.isPremium ? (
              <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-6 py-3 rounded-full shadow-lg">
                <Star className="w-5 h-5 inline mr-2" />
                Compte Premium - Recherches illimitées
              </div>
            ) : (
              <div className="bg-white rounded-full px-6 py-3 shadow-lg border border-gray-200">
                <span className="text-gray-700">
                  Recherches gratuites restantes: <strong>{sessionData?.freeSearchesRemaining || 0}</strong>
                </span>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="flex justify-center space-x-8">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Database className="w-5 h-5 text-indigo-500 mr-2" />
                <span className="text-2xl font-bold text-indigo-600">
                  {sessionData?.totalNumbers ? Math.floor(sessionData.totalNumbers * 1.2) : '100K+'}
                </span>
              </div>
              <p className="text-sm text-gray-600">Numéros</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <MapPin className="w-5 h-5 text-green-500 mr-2" />
                <span className="text-2xl font-bold text-green-600">8</span>
              </div>
              <p className="text-sm text-gray-600">Villes</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Globe className="w-5 h-5 text-purple-500 mr-2" />
                <span className="text-2xl font-bold text-purple-600">3</span>
              </div>
              <p className="text-sm text-gray-600">Opérateurs</p>
            </div>
          </div>
        </div>

        {/* Section de recherche */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="text-center mb-6">
              <Search className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                Rechercher un numéro
              </h2>
              <p className="text-gray-600">
                Formats acceptés: +237XXXXXXXX, 237XXXXXXXX, 6XXXXXXXX, XXXXXXXX
              </p>
            </div>

            <div className="space-y-4">
              <div className="relative">
                <input
                  type="text"
                  value={searchNumber}
                  onChange={(e) => setSearchNumber(e.target.value)}
                  placeholder="Ex: +237654321098, 654321098, 6543210..."
                  className="w-full px-6 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all duration-200"
                  disabled={isSearching}
                />
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                  <Phone className="w-5 h-5 text-gray-400" />
                </div>
              </div>

              <button
                onClick={() => handleSearch()}
                disabled={isSearching}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold py-4 px-6 rounded-xl hover:from-green-600 hover:to-emerald-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isSearching ? (
                  <>
                    <Clock className="w-5 h-5 inline mr-2 animate-spin" />
                    Recherche en cours...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5 inline mr-2" />
                    Vérifier le numéro
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Résultats de recherche */}
        {searchResult && (
          <div className="max-w-2xl mx-auto mb-8">
            <div className={`rounded-2xl shadow-xl p-8 border-2 transition-all duration-500 ${searchResult.found
              ? 'bg-green-50 border-green-200 animate-pulse'
              : 'bg-red-50 border-red-200'
              }`}>
              <div className="text-center">
                {searchResult.found ? (
                  <div>
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4 animate-bounce" />
                    <h3 className="text-2xl font-bold text-green-800 mb-2">
                      ✅ Numéro trouvé !
                    </h3>
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-green-200">
                      <div className="space-y-4">
                        <div className="flex items-center justify-center">
                          <Phone className="w-6 h-6 text-green-600 mr-3" />
                          <span className="text-2xl font-mono font-bold text-gray-800">
                            {formatPhoneDisplay(searchResult.data.numero)}
                          </span>
                        </div>

                        <div className="flex items-center justify-center">
                          <MapPin className={`w-5 h-5 text-${searchResult.data.cityColor}-500 mr-2`} />
                          <span className="text-lg text-gray-700">
                            {searchResult.data.cityIcon} {searchResult.data.city}
                          </span>
                        </div>


                        {/* display age and pseudo if available */}
                        {searchResult.data.age && (
                          <div className="flex items-center justify-center">
                            <span className="text-lg text-gray-700">
                              🎂 {searchResult.data.age}
                            </span>
                          </div>
                        )}
                        {searchResult.data.pseudo && (
                          <div className="flex items-center justify-center">
                            <span className="text-lg text-gray-700">
                              🆔 {searchResult.data.pseudo}
                            </span>
                          </div>
                        )}

                        {searchResult.data.operateur && (
                          <div className="flex items-center justify-center">
                            <span className={`px-4 py-2 bg-${getOperatorColor(searchResult.data.operateur)}-100 text-${getOperatorColor(searchResult.data.operateur)}-800 rounded-full text-sm font-medium`}>
                              📡 {searchResult.data.operateur}
                            </span>
                          </div>
                        )}

                        {searchResult.data.url && (
                          <div className="pt-2">
                            <a
                              href={searchResult.data.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                            >
                              🔗 Voir la source
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-red-800 mb-2">
                      ❌ Numéro non trouvé
                    </h3>
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-red-200">
                      <div className="flex items-center justify-center mb-3">
                        <Phone className="w-6 h-6 text-red-600 mr-3" />
                        <span className="text-xl font-mono font-bold text-gray-800">
                          {searchResult.number}
                        </span>
                      </div>
                      <div className="flex items-center justify-center text-red-600">
                        <AlertCircle className="w-5 h-5 mr-2" />
                        <span>Ce numéro n'existe pas dans notre base de données</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}


        {/* Modal des conditions d'utilisation */}

        {showTermsModal && !termsAccepted && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Shield className="w-8 h-8 text-indigo-500 mr-3" />
                    <h2 className="text-2xl font-bold text-gray-800">
                      Conditions d'utilisation & Avertissement
                    </h2>
                  </div>
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="space-y-6 text-left">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-gray-700 font-medium">
                      Bienvenue sur notre application. En accédant à ce service, vous acceptez pleinement les conditions suivantes.
                      Il est de votre devoir de les lire attentivement avant toute utilisation.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">1. Objectif de l'application</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Cette application a pour vocation d'aider les utilisateurs à vérifier si une personne figure sur
                      certaines plateformes de rencontre, à partir de données accessibles publiquement. Elle ne doit en
                      aucun cas être utilisée pour surveiller, harceler, diffamer ou nuire à autrui.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">2. Responsabilité de l'utilisateur</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      <li>Vous êtes seul responsable de l'usage que vous faites des informations obtenues via l'application.</li>
                      <li>Toute utilisation abusive, illégale ou contraire à l'éthique est interdite et vous expose à des sanctions civiles ou pénales.</li>
                      <li>L'application est un outil d'aide à la recherche, et ne doit pas être utilisée comme un instrument de vengeance ou d'intimidation.</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">3. Confidentialité, données personnelles et paiements</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      <li>Nous collectons uniquement les informations nécessaires au bon fonctionnement de l'application.</li>
                      <li>Lorsque vous effectuez un paiement pour accéder à des fonctionnalités premium, <strong>le numéro de téléphone utilisé pour le paiement peut être enregistré</strong>. Ce numéro nous permet :
                        <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                          <li>De vérifier votre accès aux services premium.</li>
                          <li>De sécuriser votre accès en cas de perte d'identifiants ou de litige.</li>
                        </ul>
                      </li>
                      <li>Ce numéro n'est <strong>jamais partagé ni utilisé à des fins publicitaires</strong>. Il est conservé de manière sécurisée et conformément aux réglementations en vigueur en matière de protection des données (comme le RGPD, si applicable).</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">4. Respect de la vie privée des tiers</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      <li>Vous vous engagez à respecter les droits à la vie privée des personnes concernées.</li>
                      <li>Toute utilisation visant à harceler, menacer, nuire ou porter atteinte à l'honneur d'un tiers est strictement interdite.</li>
                      <li>Vous êtes tenu de respecter les lois en vigueur dans votre pays concernant la protection de la vie privée.</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">5. Limitation de responsabilité</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      <li>Les informations fournies par l'application proviennent de sources accessibles publiquement ou semi-publiquement. Leur exactitude ou actualité ne peut être garantie.</li>
                      <li>L'équipe de développement décline toute responsabilité concernant les conséquences de l'utilisation que vous faites de ces informations.</li>
                      <li>Vous utilisez cette application sous votre entière responsabilité.</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">6. Acceptation des conditions</h3>
                    <p className="text-gray-700 leading-relaxed">
                      En utilisant cette application, vous confirmez avoir lu, compris et accepté l'ensemble des conditions d'utilisation.
                      Si vous êtes en désaccord avec l'un de ces termes, veuillez ne pas utiliser ce service.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex space-x-4">
                  <button
                    onClick={handleRejectTerms}
                    className="flex-1 bg-gray-200 text-gray-800 font-semibold py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Refuser
                  </button>
                  {/* <button
                      onClick={handleAcceptTerms}
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Accepter
                    </button> */}
                  <button
                    onClick={handleAcceptTerms}
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-sem
ibold py-3 px-6 rounded-lg hover:from-green-600 hover:to-emerald-700 transition-colors"
                  >
                    Accepter
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Modal de paiement */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
              <div className="text-center">
                {paymentStatus === 'pending' ? (
                  <div>
                    <Clock className="w-16 h-16 text-blue-500 mx-auto mb-4 animate-spin" />
                    <h3 className="text-2xl font-bold text-gray-800 mb-4">
                      Paiement en cours...
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Veuillez confirmer le paiement sur votre téléphone
                    </p>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm text-blue-800">
                        Montant: <strong>{sessionData?.paymentAmount} FCFA</strong>
                      </p>
                      <p className="text-sm text-blue-800">
                        Transaction: {paymentData?.transactionId?.substring(0, 8)}...
                      </p>
                    </div>
                  </div>
                ) : paymentStatus === 'success' ? (
                  <div>
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4 animate-bounce" />
                    <h3 className="text-2xl font-bold text-green-800 mb-4">
                      Paiement réussi !
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Votre compte premium est maintenant activé
                    </p>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm text-green-800">
                        🎉 Recherches illimitées débloquées !
                      </p>
                      <p className="text-xs text-green-600 mt-2">
                        Redirection automatique en cours...
                      </p>
                    </div>
                  </div>
                ) : (
                  <PaymentForm
                    onPayment={initiatePayment}
                    onCancel={handleClosePaymentModal}
                    amount={sessionData?.paymentAmount}
                    isLoading={isPaymentLoading}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Historique de recherche */}
        {searchHistory.length > 0 && (
          <div className="max-w-2xl mx-auto mb-8">
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Historique des recherches
              </h3>
              <div className="space-y-2">
                {searchHistory.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      {item.found ? (
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500 mr-2" />
                      )}
                      <span className="font-mono text-sm">{item.number}</span>
                      {item.found && (
                        <span className="ml-2 text-xs text-gray-600">
                          → {item.data.city}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">{item.timestamp}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Cities Overview */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <h3 className="text-2xl font-semibold text-gray-800 mb-6 text-center">
              Quelques villes couvertes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {Object.entries(cities).map(([key, city]) => (
                <div key={key} className="text-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="text-3xl mb-2">{city.icon}</div>
                  <h4 className="font-medium text-gray-800">{city.name}</h4>
                  {/* <p className="text-sm text-gray-600">
                    {phoneData[key]?.length || 0} numéros
                  </p> */}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Avantages Premium
        {!sessionData?.isPremium && (
          <div className="max-w-2xl mx-auto mb-8">
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl shadow-xl p-8 border border-yellow-200">
              <div className="text-center">
                <Star className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Passez au Premium
                </h3>
                <p className="text-gray-600 mb-6">
                  Débloquez toutes les fonctionnalités pour seulement {sessionData?.paymentAmount} FCFA
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <CheckCircle className="w-6 h-6 text-green-500 mb-2" />
                    <h4 className="font-semibold text-gray-800">Recherches illimitées</h4>
                    <p className="text-sm text-gray-600">Aucune limite sur vos vérifications</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <CheckCircle className="w-6 h-6 text-green-500 mb-2" />
                    <h4 className="font-semibold text-gray-800">Support prioritaire</h4>
                    <p className="text-sm text-gray-600">Assistance rapide et dédiée</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold py-3 px-8 rounded-xl hover:from-yellow-600 hover:to-orange-600 transform hover:scale-105 transition-all duration-200 shadow-lg"
                >
                  <CreditCard className="w-5 h-5 inline mr-2" />
                  Activer Premium
                </button>
              </div>
            </div>
          </div>
        )} */}

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-gray-200">
          <p className="text-gray-500 mb-2">
            Vérificateur de numéros de téléphone sur Jedolo
          </p>
          <p className="text-sm text-gray-400">
            Sécurisé • Rapide • Fiable
          </p>

          <div className="mt-8 text-center">
            <p className="text-gray-500 mb-2">
              © 2025 Jedo Me
            </p>
            <p className="text-sm text-gray-400">
              Jedo Me est un projet fais avec le coeur ♥️ et pour aider
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentForm = ({ onPayment, onCancel, amount, isLoading }) => {
  const [paymentPhone, setPaymentPhone] = useState('');
  const [selectedOperator, setSelectedOperator] = useState('AUTO');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!paymentPhone.trim()) {
      alert('Veuillez entrer votre numéro de paiement');
      return;
    }
    onPayment(paymentPhone, selectedOperator);
  };

  return (
    <div>
      <CreditCard className="w-16 h-16 text-indigo-500 mx-auto mb-4" />
      <h3 className="text-2xl font-bold text-gray-800 mb-4">
        Paiement sécurisé
      </h3>
      <p className="text-gray-600 mb-6">
        Accès premium pour <strong>{amount} FCFA</strong>
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 text-start">
            Numéro de paiement Mobile Money
          </label>
          <input
            type="tel"
            value={paymentPhone}
            onChange={(e) => setPaymentPhone(e.target.value)}
            placeholder="Ex: 654321098"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            required
            disabled={isLoading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 text-start">
            Opérateur
          </label>
          <select
            value={selectedOperator}
            onChange={(e) => setSelectedOperator(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            disabled={isLoading}
          >
            <option value="AUTO">Détection automatique</option>
            <option value="MTN">MTN Mobile Money</option>
            <option value="Orange">Orange Money</option>
          </select>
        </div>

        <div className="flex space-x-4 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-gray-200 text-gray-800 font-semibold py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            Annuler
          </button>
          <button
            type="submit"
            className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Clock className="w-4 h-4 inline mr-2 animate-spin" />
                Traitement...
              </>
            ) : (
              <>
                Payer {amount} FCFA
              </>
            )}
          </button>
        </div>
      </form>

      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
        <p className="text-xs text-blue-800">
          🔒 Paiement 100% sécurisé • Activation instantanée
        </p>
      </div>
    </div>
  );
};

export default PhoneVerifierApp;